import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Plus, 
  Eye,
  Edit,
  MoreVertical,
  GraduationCap,
  Clock,
  BarChart3,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import TeacherLayout from '../../components/layout/TeacherLayout';
import teacherBatchSync from '../../services/teacherBatchSync';
import { getBatchEnrollments } from '../../firebase/collections';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

const TeacherBatches = () => {
  const { currentUser, userProfile } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchStats, setBatchStats] = useState({});

  useEffect(() => {
    if (currentUser?.uid && userProfile?.role === 'teacher') {
      loadTeacherBatches();
      
      // Subscribe to real-time batch updates
      const unsubscribe = teacherBatchSync.subscribeToTeacherBatches(
        currentUser.uid,
        (updatedBatches) => {
          console.log('Real-time batch update received:', updatedBatches.length, 'batches');
          setBatches(updatedBatches);
          loadBatchStats(updatedBatches);
        }
      );
      
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [currentUser, userProfile]);

  // Subscribe to individual batch changes when batch list changes
  useEffect(() => {
    if (batches.length > 0) {
      const batchIds = batches.map(batch => batch.id).filter(id => id);
      
      if (batchIds.length > 0) {
        const unsubscribe = teacherBatchSync.subscribeToMultipleBatches(
          batchIds,
          (updatedBatches) => {
            setBatches(updatedBatches);
            loadBatchStats(updatedBatches);
          }
        );
        
        return () => unsubscribe();
      }
    }
  }, [batches.map(b => b.id).join(',')]);

  const loadTeacherBatches = async () => {
    try {
      setLoading(true);
      
      if (!currentUser?.uid) {
        setBatches([]);
        setBatchStats({});
        return;
      }
      
      // Query batches where teacherIds array contains current user
      const batchesQuery = query(
        collection(db, 'batches'),
        where('teacherIds', 'array-contains', currentUser.uid)
      );
      
      const batchesSnapshot = await getDocs(batchesQuery);
      const batchesList = [];
      
      batchesSnapshot.forEach((doc) => {
        const batchData = doc.data();
        batchesList.push({
          id: doc.id,
          ...batchData
        });
      });
      
      setBatches(batchesList);
      await loadBatchStats(batchesList);
      
    } catch (error) {
      console.error('Error loading teacher batches:', error);
      setBatches([]);
      setBatchStats({});
    } finally {
      setLoading(false);
    }
  };

  const loadBatchStats = async (batchList) => {
    const stats = {};
    
    for (const batch of batchList) {
      try {
        if (!batch.id) {
          continue;
        }
        
        // Use same logic as TeacherBatchManagement - try getBatchEnrollments first
        let realStudentCount = 0;
        try {
          const enrollmentsResult = await getBatchEnrollments(batch.id);
          
          if (enrollmentsResult.success && enrollmentsResult.data && enrollmentsResult.data.length > 0) {
            realStudentCount = enrollmentsResult.data.length;
            console.log(`Batch ${batch.id} (${batch.name}) has ${realStudentCount} enrolled students (from getBatchEnrollments)`);
          } else {
            // Fallback to teacherBatchSync method
            const studentsFromSync = await teacherBatchSync.getBatchStudents(batch.id);
            realStudentCount = studentsFromSync.length;
            console.log(`Batch ${batch.id} (${batch.name}) has ${realStudentCount} students (from getBatchStudents fallback)`);
          }
        } catch (enrollmentError) {
          console.log(`Error with getBatchEnrollments for batch ${batch.id}, using getBatchStudentCount fallback`);
          realStudentCount = await teacherBatchSync.getBatchStudentCount(batch.id);
        }
        
        // Load real stats from Firestore
        const [
          attendanceRate,
          averageScore,
          pendingAssignments,
          upcomingClasses,
          pendingDoubts
        ] = await Promise.all([
          teacherBatchSync.getBatchAttendanceRate(batch.id),
          teacherBatchSync.getBatchAverageScore(batch.id),
          teacherBatchSync.getBatchPendingAssignments(batch.id),
          teacherBatchSync.getBatchUpcomingClasses(batch.id),
          teacherBatchSync.getBatchPendingDoubts(batch.id)
        ]);
        
        stats[batch.id] = {
          totalStudents: realStudentCount,
          activeStudents: realStudentCount, // All students are considered active unless specified otherwise
          attendanceRate: attendanceRate,
          averageScore: averageScore,
          upcomingClasses: upcomingClasses.length,
          pendingAssignments: pendingAssignments,
          pendingDoubts: pendingDoubts.length
        };
        
      } catch (error) {
        // Fallback to any available student count data
        const fallbackCount = batch.studentCount || batch.totalStudents || batch.students?.length || 0;
        stats[batch.id] = {
          totalStudents: fallbackCount,
          activeStudents: fallbackCount,
          attendanceRate: 0,
          averageScore: 0,
          upcomingClasses: 0,
          pendingAssignments: 0,
          pendingDoubts: 0
        };
      }
    }
    
    setBatchStats(stats);
  };

  return (
    <TeacherLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Batches</h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your assigned batches
                {batches.length > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    • {batches.length} batch{batches.length !== 1 ? 'es' : ''} • {Object.values(batchStats).reduce((sum, stats) => sum + (stats.totalStudents || 0), 0)} total students
                  </span>
                )}
              </p>
            </div>

          </div>
        </div>

        {/* Summary Stats */}
        {!loading && batches.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Total Batches</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{batches.length}</p>
                </div>
                <GraduationCap className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Total Students</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {Object.values(batchStats).reduce((sum, stats) => sum + (stats.totalStudents || 0), 0)}
                  </p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </div>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">Avg Students/Batch</p>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                    {Math.round(Object.values(batchStats).reduce((sum, stats) => sum + (stats.totalStudents || 0), 0) / batches.length) || 0}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Active Batches</p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{batches.length}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        )}

        {/* Batches Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {batches.length > 0 ? (
              batches.map((batch, index) => {
                const stats = batchStats[batch.id] || {};
                
                return (
                  <motion.div
                    key={batch.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                          <GraduationCap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {batch.name || `Batch ${index + 1}`}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {batch.subject || 'Subject not specified'}
                          </p>
                        </div>
                      </div>
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Batch Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <Users className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {stats.totalStudents || batch.studentCount || batch.totalStudents || 0}
                          </span>
                        </div>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">Students</p>
                      </div>
                      
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-900 dark:text-green-100">
                            {stats.attendanceRate || 0}%
                          </span>
                        </div>
                        <p className="text-xs text-green-700 dark:text-green-300 mt-1">Attendance</p>
                      </div>
                      
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <BarChart3 className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                            {stats.averageScore || 0}%
                          </span>
                        </div>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">Avg Score</p>
                      </div>
                      
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <Clock className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                            {stats.upcomingClasses || 0}
                          </span>
                        </div>
                        <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">Upcoming</p>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Assigned Date</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {batch.assignedAt ? new Date(batch.assignedAt).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      {stats.pendingAssignments > 0 && (
                        <div className="flex items-center space-x-2 text-sm">
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                          <span className="text-orange-600 dark:text-orange-400">
                            {stats.pendingAssignments} pending assignments
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Link
                        to={`/teacher/batch/${batch.id || index}`}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-center text-sm font-medium transition-colors"
                      >
                        <Eye className="w-4 h-4 inline mr-1" />
                        Manage
                      </Link>

                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-12">
                <GraduationCap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Batches Assigned
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You haven't been assigned to any batches yet. Contact your coaching administrator.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your coaching admin can assign batches to you from the teacher management panel
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </TeacherLayout>
  );
};

export default TeacherBatches;